import { createContext, memo, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

type EditCanvasDropzoneContextValue = {
  isActive: boolean;
  isFullscreen: boolean;
  setActive: () => () => void;
  setFullscreen: (value: boolean) => void;
};

const EditCanvasDropzoneContext = createContext<EditCanvasDropzoneContextValue>({
  isActive: false,
  isFullscreen: false,
  setActive: () => () => {},
  setFullscreen: () => {},
});

export type EditCanvasDropzoneProviderProps = {
  children: ReactNode;
};

export const EditCanvasDropzoneProvider = memo(({ children }: EditCanvasDropzoneProviderProps) => {
  const [isActive, setIsActive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const setActive = useCallback(() => {
    setIsActive(true);
    return () => setIsActive(false);
  }, []);

  const setFullscreen = useCallback((value: boolean) => {
    setIsFullscreen(value);
  }, []);

  const value = useMemo(
    () => ({ isActive, isFullscreen, setActive, setFullscreen }),
    [isActive, isFullscreen, setActive, setFullscreen],
  );

  return <EditCanvasDropzoneContext.Provider value={value}>{children}</EditCanvasDropzoneContext.Provider>;
});

EditCanvasDropzoneProvider.displayName = 'EditCanvasDropzoneProvider';

/** Returns whether the edit-canvas file dropzone is currently active. */
export function useEditCanvasDropzoneActive() {
  return useContext(EditCanvasDropzoneContext).isActive;
}

/**
 * Returns a function that marks the edit-canvas dropzone as active.
 * Call the returned function on mount; it returns a cleanup function for unmount.
 */
export function useSetEditCanvasDropzoneActive() {
  return useContext(EditCanvasDropzoneContext).setActive;
}

/** Returns whether the edit-canvas is currently in fullscreen mode. */
export function useEditCanvasDropzoneFullscreen() {
  return useContext(EditCanvasDropzoneContext).isFullscreen;
}

/** Returns a setter for the edit-canvas fullscreen state. */
export function useSetEditCanvasDropzoneFullscreen() {
  return useContext(EditCanvasDropzoneContext).setFullscreen;
}
